import React, { FC, useEffect, useState } from "react"
import { Form, Input, InputNumber, Modal, Select } from "antd"
import { observer } from "mobx-react"
import { notifications } from "notifications"
import { SubmitCancelButtons, GameSelect } from "components"
import { useStore } from "stores/utils/use-store"
import { GameModeMultiplier } from "../store/types"
import { GAMEMODE_PARAMETER_PRESETS, isPresetGamemodeParameter } from "abstract/constants/gamemode-parameter"

interface CreateEditGameModeMultiplierModalProps {
  visible: boolean
  onClose: () => void
  initialData?: GameModeMultiplier | null
}

export const CreateEditGameModeMultiplierModal: FC<CreateEditGameModeMultiplierModalProps> = observer(({
  visible,
  onClose,
  initialData,
}) => {
  const { dataStore: { gameModeMultipliersStore } } = useStore()
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [parameterType, setParameterType] = useState<"preset" | "custom">("preset")

  useEffect(() => {
    if (visible) {
      if (initialData) {
        const currentParam = initialData.gameModeParameter || "gameMode"
        const isPreset = isPresetGamemodeParameter(currentParam)
        setParameterType(isPreset ? "preset" : "custom")
        form.setFieldsValue({
          gameId: initialData.gameId,
          gameMode: initialData.gameMode,
          multiplier: initialData.multiplier,
          gameModeParameter: currentParam,
        })
      } else {
        form.resetFields()
        form.setFieldsValue({ gameModeParameter: "gameMode" })
        setParameterType("preset")
      }
    }
  }, [visible, initialData, form])

  const onFinishHandler = async(values: any) => {
    if (submitting) return

    setSubmitting(true)

    try {
      const trimmedgameModeParameter = values.gameModeParameter?.trim() || "gameMode"

      if (initialData) {
        const data = {
          multiplier: Number(values.multiplier),
          gameModeParameter: trimmedgameModeParameter,
        }
        await gameModeMultipliersStore.updateGameModeMultiplier(initialData.id, data)
        notifications.success("Game mode multiplier updated")
      } else {
        const data = {
          gameId: Number(values.gameId),
          gameMode: values.gameMode.trim(),
          multiplier: Number(values.multiplier),
          gameModeParameter: trimmedgameModeParameter,
        }
        await gameModeMultipliersStore.createGameModeMultiplier(data)
        notifications.success("Game mode multiplier created")
      }
      setSubmitting(false)
      onClose()
      form.resetFields()
    } catch (error: any) {
      notifications.error(error?.cause?.message || error?.message || "Failed to save game mode multiplier")
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      open={visible}
      title={initialData ? "Edit Game Mode Multiplier" : "Create Game Mode Multiplier"}
      width="700px"
      footer={null}
      onCancel={handleClose}
      style={{ top: "10px" }}
    >
      <Form
        form={form}
        onFinish={onFinishHandler}
        layout="vertical"
        id="createEditGameModeMultiplier"
      >
        <Form.Item
          label="Game ID"
          name="gameId"
          rules={[
            {
              required: true,
              message: "Please select a game",
            },
          ]}
        >
          {initialData ? (
            <Input value={initialData.gameId} disabled />
          ) : (
            <GameSelect />
          )}
        </Form.Item>

        <Form.Item
          label="Gamemode Parameter Type"
        >
          <Select
            value={parameterType}
            onChange={value => {
              setParameterType(value)
              if (value === "preset") {
                form.setFieldsValue({ gameModeParameter: "gameMode" })
              } else {
                form.setFieldsValue({ gameModeParameter: "" })
              }
            }}
          >
            <Select.Option value="preset">Preset</Select.Option>
            <Select.Option value="custom">Custom</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Gamemode Parameter"
          name="gameModeParameter"
          initialValue="gameMode"
          rules={[
            {
              required: true,
              message: "Please enter a gamemode parameter",
            },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve()
                }
                const trimmed = value.trim()
                if (!trimmed) {
                  return Promise.reject(new Error("Gamemode parameter cannot be empty or whitespace only"))
                }

                const identifierPattern = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/
                if (!identifierPattern.test(trimmed)) {
                  return Promise.reject(new Error("Gamemode parameter must be a valid identifier (letters, numbers, underscore, $)"))
                }

                return Promise.resolve()
              },
            },
          ]}
        >
          {parameterType === "preset" ? (
            <Select>
              {GAMEMODE_PARAMETER_PRESETS.map(p => (
                <Select.Option key={p} value={p}>{p}</Select.Option>
              ))}
            </Select>
          ) : (
            <Input placeholder="Enter custom parameter name" />
          )}
        </Form.Item>

        <Form.Item
          label="Game Mode"
          name="gameMode"
          rules={[
            {
              required: true,
              message: "Please enter a game mode",
            },
          ]}
        >
          <Input placeholder="Enter game mode name" disabled={!!initialData} />
        </Form.Item>

        <Form.Item
          label="Multiplier"
          name="multiplier"
          rules={[
            {
              required: true,
              message: "Please enter a multiplier",
            },
            {
              validator: (_, value) => {
                const numValue = Number(value)
                if (value === null || value === undefined || isNaN(numValue)) {
                  return Promise.reject(new Error("Please enter a valid multiplier"))
                }
                if (numValue <= 0) {
                  return Promise.reject(new Error("Multiplier must be greater than 0"))
                }

                return Promise.resolve()
              },
            },
          ]}
        >
          <InputNumber style={{ width: "100%" }} min={0.1} step={0.1} placeholder="Enter multiplier value" />
        </Form.Item>

        <SubmitCancelButtons
          formId="createEditGameModeMultiplier"
          onClose={handleClose}
          isLoading={submitting || gameModeMultipliersStore.isLoading}
        />
      </Form>
    </Modal>
  )
})
